import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Zap, Lock } from 'lucide-react';
import { usePageContent } from '@/hooks/usePageContent';
import EditableText from '@/components/EditableText';
import { useNavigate } from 'react-router-dom';

const defaults = {
  badge: 'Next-Gen IT Security',
  heading_line1: 'Your business.',
  heading_line2: 'Impenetrable.',
  description: "We don't just manage IT\u2014we weaponize it. Real-time threat detection, zero-friction support, and infrastructure that scales with your ambition.",
  cta_primary: 'Deploy Your Shield',
  cta_secondary: 'Calculate Your Investment',
  trust_item_1: 'SOC 2 Certified',
  trust_item_2: '< 15 Min Response',
  trust_item_3: 'Zero Trust Architecture',
};

const HeroSection = () => {
  const [init, setInit] = useState(false);
  const { content: c } = usePageContent('home', 'home', { hero: defaults });
  const hero = c.hero || defaults;
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    let cancelHandle;
    const arm = () => {
      if (cancelled) return;
      initParticlesEngine(async (engine) => {
        await loadSlim(engine);
      }).then(() => { if (!cancelled) setInit(true); });
    };
    // Defer expensive particles engine init until the browser is idle so it
    // doesn't compete with hero LCP/critical paint on mobile.
    const ric = window.requestIdleCallback;
    if (typeof ric === 'function') {
      cancelHandle = ric(arm, { timeout: 2500 });
    } else {
      cancelHandle = setTimeout(arm, 1200);
    }
    return () => {
      cancelled = true;
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(cancelHandle);
      else clearTimeout(cancelHandle);
    };
  }, []);

  const particlesOptions = useMemo(() => {
    // Halve particle count on mobile/tablet — dramatically reduces TBT and
    // continuous main-thread work without affecting desktop visuals.
    const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    return ({
    background: { color: { value: 'transparent' } },
    fpsLimit: isMobile ? 30 : 60,
    particles: {
      color: { value: ['#E63946', '#ffffff', '#ff6b6b'] },
      links: { color: '#E63946', distance: 150, enable: true, opacity: 0.2, width: 1 },
      move: { enable: true, speed: 1, direction: 'none', random: true, straight: false, outModes: { default: 'bounce' } },
      number: { density: { enable: true, area: 800 }, value: isMobile ? 25 : 60 },
      opacity: { value: 0.5 },
      shape: { type: 'circle' },
      size: { value: { min: 1, max: 3 } },
    },
    detectRetina: true,
  });
  }, []);

  const scrollTo = (id) => {
    const go = (smooth) => {
      const el = document.querySelector(id);
      if (el) el.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
    };
    go(true);
    setTimeout(() => go(false), 1000);
    setTimeout(() => go(true), 2000);
  };

  const trustItems = [
    { icon: Shield, text: hero.trust_item_1 || defaults.trust_item_1 },
    { icon: Zap, text: hero.trust_item_2 || defaults.trust_item_2 },
    { icon: Lock, text: hero.trust_item_3 || defaults.trust_item_3 },
  ];

  return (
    <section className="relative min-h-screen flex items-center hero-gradient overflow-hidden" data-testid="hero-section">
      {init && (
        <Particles id="tsparticles" className="absolute inset-0 z-0" options={particlesOptions} />
      )}
      <div className="absolute inset-0 grid-bg opacity-50 z-0" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <EditableText pageType="home" pageId="home" field="hero.badge" as="span" className="text-xs tracking-[0.2em] uppercase font-bold text-[#E63946] mb-6 block">
                {hero.badge || defaults.badge}
              </EditableText>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium text-white mb-6"
              data-testid="hero-heading"
            >
              <EditableText pageType="home" pageId="home" field="hero.heading_line1" as="span">
                {hero.heading_line1 || defaults.heading_line1}
              </EditableText><br />
              <EditableText pageType="home" pageId="home" field="hero.heading_line2" as="span" className="gradient-text">
                {hero.heading_line2 || defaults.heading_line2}
              </EditableText>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            >
              <EditableText
                pageType="home" pageId="home" field="hero.seo_subheading" as="h2" richText
                className="text-base sm:text-lg text-gray-300 font-medium mb-5 max-w-2xl"
                data-testid="hero-seo-h2"
              >
                {hero.seo_subheading || 'Managed IT Services, Cybersecurity & Microsoft 365 Support for Vancouver and Canadian Businesses.'}
              </EditableText>
            </motion.div>
            <EditableText pageType="home" pageId="home" field="hero.description" as="p" richText
              className="text-gray-400 text-lg lg:text-xl max-w-xl mb-10 leading-relaxed"
              data-testid="hero-subtext">
              {hero.description || defaults.description}
            </EditableText>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => navigate('/contact-us')} className="btn-primary-new px-8 py-6 text-base rounded-none pulse-glow" data-testid="hero-cta-primary">
                {hero.cta_primary || defaults.cta_primary}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => scrollTo('#calculator')} variant="outline" className="px-8 py-6 text-base rounded-none border-white/20 hover:border-white/40 bg-transparent text-white" data-testid="hero-cta-secondary">
                {hero.cta_secondary || defaults.cta_secondary}
              </Button>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }} className="flex flex-wrap gap-6 mt-12">
              {trustItems.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-400">
                  <item.icon className="w-4 h-4 text-[#E63946]" />
                  <span>{item.text}</span>
                </div>
              ))}
            </motion.div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.4 }} className="relative">
              <div className="relative w-full h-[500px]">
                <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-[#E63946]/20 border border-[#E63946]/50 flex items-center justify-center glow-red">
                  <Shield className="w-20 h-20 text-[#E63946]" />
                </motion.div>
                {[0, 1, 2, 3].map((i) => (
                  <motion.div key={i} animate={{ rotate: 360 }} transition={{ duration: 20 + i * 5, repeat: Infinity, ease: 'linear' }} style={{ transformOrigin: 'center center' }} className="absolute top-1/2 left-1/2 w-full h-full -translate-x-1/2 -translate-y-1/2">
                    <div className={`absolute w-4 h-4 bg-[#E63946] ${i % 2 === 0 ? 'opacity-100' : 'opacity-50'}`} style={{ top: `${20 + i * 15}%`, left: `${10 + i * 20}%` }} />
                  </motion.div>
                ))}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 500">
                  <motion.path d="M 200 50 Q 350 150 200 250 Q 50 350 200 450" fill="none" stroke="#E63946" strokeWidth="1" strokeDasharray="5,5" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.3 }} transition={{ duration: 2, delay: 0.5 }} />
                </svg>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-500 text-xs tracking-widest uppercase">
        Scroll to explore
      </motion.div>
    </section>
  );
};

export default HeroSection;
